/**
 * HeldFactorStep — the ONE renderer of the factor a held sign-in sets up IN PLACE (SECREQ S6c (d), D6).
 *
 * A sign-in the company MFA policy holds is answered by that factor's own step, over the shared auth context:
 *   - two-step verification — mfa-ui's `MfaEnrollment` (`auth.mfaEnrollmentRequired`);
 *   - a passkey — mfa-ui's `PasskeyEnrollment` (`auth.factorEnrollment.factor === 'passkey'`);
 *   - a PIN — the HOST's step (`pinEnrollmentStep`; a PIN is the host's credential, so login-shell renders none), by
 *     the ONE rule `pinEnrollmentStepOf`, which refuses a PIN hold with no step by name.
 * login-shell's own form renders it (`secondFactorStep`), and so does a host that runs a sign-in of its own over the
 * shared context (pos-web's first-time-setup wizard), so the three steps have exactly one renderer. WHETHER a hold is
 * answered in place (a passkey in an app that sets none up is a handoff instead) is the caller's to decide; this
 * renders the step for the hold the context holds, and nothing when it holds none.
 *
 * @module @puffin-core/login-shell/components/HeldFactorStep
 */
import PropTypes from 'prop-types';
import { MfaEnrollment } from '@puffin-core/mfa-ui/enrollment';
import { PasskeyEnrollment } from '@puffin-core/mfa-ui/passkey-enrollment';
import { pinEnrollmentStepOf } from '../hooks/useLoginForm.js';

/**
 * @param {Object} props
 * @param {Object} props.auth - The shared auth context (`useAuth()`), holding the hold and each factor's enrolment.
 * @param {string} [props.appName] - The brand lockup the enrolment screens name.
 * @param {boolean} [props.loading=false] - A step is in flight.
 * @param {*} [props.error=null] - The MFA enrolment's own refusal, for its screen to word.
 * @param {?React.ElementType} [props.pinEnrollmentStep=null] - The host's PIN step.
 * @param {(factor: 'mfa'|'passkey'|'pin') => void} props.onFinish - The host's sign-in tail, named by the factor.
 * @param {() => void} props.onCancel - Drops the hold.
 * @param {Function} [props.t] - The consumer translator.
 * @returns {?React.JSX.Element}
 */
export function HeldFactorStep({
  auth,
  appName,
  loading = false,
  error = null,
  pinEnrollmentStep = null,
  onFinish,
  onCancel,
  t,
}) {
  if (auth.mfaEnrollmentRequired) {
    return (
      <MfaEnrollment
        appName={appName}
        enrollment={auth.mfaEnrollment}
        loading={loading}
        error={error}
        onBegin={auth.beginMfaEnrollment}
        onComplete={auth.completeMfaEnrollment}
        onFinish={() => onFinish('mfa')}
        onCancel={onCancel}
        t={t}
      />
    );
  }
  if (auth.factorEnrollment?.factor === 'passkey') {
    return (
      <PasskeyEnrollment
        appName={appName}
        enrollment={auth.passkeyEnrollment}
        loading={loading}
        onBegin={auth.beginPasskeyEnrollment}
        onComplete={auth.completePasskeyEnrollment}
        onFinish={() => onFinish('passkey')}
        onCancel={onCancel}
        t={t}
      />
    );
  }
  const pin = pinEnrollmentStepOf(auth, pinEnrollmentStep, () => onFinish('pin'));
  if (pin) {
    const { Step } = pin;
    return (
      <Step
        enrollment={auth.pinEnrollment}
        loading={loading}
        onComplete={auth.completePinEnrollment}
        onFinish={pin.onFinish}
        onCancel={onCancel}
      />
    );
  }
  return null;
}

HeldFactorStep.propTypes = {
  auth: PropTypes.shape({
    mfaEnrollmentRequired: PropTypes.bool,
    mfaEnrollment: PropTypes.object,
    beginMfaEnrollment: PropTypes.func,
    completeMfaEnrollment: PropTypes.func,
    factorEnrollment: PropTypes.shape({ factor: PropTypes.string.isRequired }),
    passkeyEnrollment: PropTypes.object,
    beginPasskeyEnrollment: PropTypes.func,
    completePasskeyEnrollment: PropTypes.func,
    pinEnrollment: PropTypes.shape({ completed: PropTypes.bool.isRequired }),
    completePinEnrollment: PropTypes.func,
  }).isRequired,
  appName: PropTypes.string,
  loading: PropTypes.bool,
  error: PropTypes.any,
  pinEnrollmentStep: PropTypes.elementType,
  onFinish: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  t: PropTypes.func,
};

export default HeldFactorStep;
